import { Fragment, FunctionalComponent, h, VNode } from 'preact';
import RatingBar from '@components/molecules/rating-bar/rating-bar';
import { useRef } from 'preact/hooks';
import { FeedbackState } from '@state/feedbackState';
import {
  CookiePolicyText,
  FeedbackFormContainer,
  FeedbackFormHeading,
  FeedbackFormLogo,
  FeedbackFormTextArea,
} from './feedback-form.styles';
import { signal, useSignal } from '@preact/signals';
import { useBrandSettings } from '@hooks/useBrandSettings';
import Icon from '@components/atoms/icon/icon';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useEnv } from '@hooks/useEnv';
import { handleNext as next } from './feedback-form';
import { useScreen } from '@hooks/useScreen';
import LoadingButton from '@components/atoms/loading-button/loading-button';

export interface FeedbackFormRatingsProps {
  useCaptcha?: boolean;
}

const selected = signal(false);

export const resetRatingSelection = (): void => {
  selected.value = false;
};

const token = signal(null);

export const getCaptchaToken = (): string | null => {
  return token.value;
};

export const resetCaptchaToken = (): void => {
  token.value = null;
};

const FeedbackFormRatings: FunctionalComponent<FeedbackFormRatingsProps> = ({
  useCaptcha = true,
}): VNode => {
  const { brand, cookiePolicyUrl } = useBrandSettings();
  const { isDevEnv, CAPTCHA_TEST_TOKEN, CAPTCHA_TOKEN } = useEnv();
  const { isTablet } = useScreen();

  const { feedback, isFeedbackValid } = FeedbackState.value;

  const isEmpty = useSignal(true);
  const isLoading = useSignal(false);

  const captchaRef = useRef(null);

  const handleCaptcha = async () => {
    captchaRef.current.execute();
  };

  const handleOnCaptchaVerify = (t: string, ekey: string) => {
    token.value = t;
    setTimeout(() => {
      next();
    }, 1000);
  };

  const handleOnCaptchaClose = () => {
    isLoading.value = false;
  };

  const handleFeedbackTextChange = (event) => {
    feedback.value.feedbackText.value = event.target.value;

    isEmpty.value = isFeedbackValid.value;
  };

  const handleSendFeedbackButtonClicked = () => {
    isLoading.value = true;
    if (useCaptcha) {
      handleCaptcha();
    }
    if (
      feedback.value.rating == 'initial' ||
      (useCaptcha && token.value == null) ||
      !isFeedbackValid.value
    ) {
      return;
    }
    captchaRef.current?.resetCaptcha();
    token.value = null;
    next();
  };

  return (
    <FeedbackFormContainer className="feedback-form" $isMobile={isTablet}>
      <FeedbackFormLogo className="feedback-form-logo">
        <Icon name={brand} />
      </FeedbackFormLogo>
      {!selected.value && (
        <FeedbackFormHeading className="feedback-form-heading">
          How would you rate your experience on our website?
        </FeedbackFormHeading>
      )}
      <RatingBar
        onSelect={(rating, number) => {
          selected.value = feedback.value.rating == rating ? !selected.value : true;
          feedback.value.rating = rating;
          feedback.value.rank = number;
        }}
      />
      {selected.value && (
        <Fragment>
          <FeedbackFormTextArea
            value={feedback.value.feedbackText.value}
            onChange={handleFeedbackTextChange}
            placeholder="Tell us about your experience..."
          />
          <LoadingButton
            type="gradient"
            onClick={handleSendFeedbackButtonClicked}
            disabled={!isFeedbackValid.value}
            isLoading={isLoading.value}
          >
            Send Feedback
          </LoadingButton>
          {useCaptcha && (
            <Fragment>
              <HCaptcha
                sitekey={isDevEnv ? CAPTCHA_TEST_TOKEN : CAPTCHA_TOKEN}
                size="invisible"
                onVerify={handleOnCaptchaVerify}
                onClose={handleOnCaptchaClose}
                ref={captchaRef}
              />
              <CookiePolicyText>
                This site is protected by hCaptcha and its Privacy Policy and Terms of Service apply
                except as noted in our{' '}
                <a href={cookiePolicyUrl} target="_blank" style={{ color: 'inherit' }}>
                  Cookie Policy.
                </a>
              </CookiePolicyText>
            </Fragment>
          )}
        </Fragment>
      )}
    </FeedbackFormContainer>
  );
};

export default FeedbackFormRatings;
